import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, FileText, Upload, Download, CheckCircle2, ExternalLink } from 'lucide-react';
import { opportunitiesAPI } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import RichTextEditor from './RichTextEditor';

// «Fund it» by commitment letter (s158). The investor commits an amount; the
// API emails them a commitment letter with the owner's bank-details PDF and
// the owner marks the deposit as received. No card payment here.

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
// Authenticated documents: fetched with the session, opened in a new tab.
const openLetter = async (cid) => {
  const w = window.open('', '_blank');
  try { const r = await opportunitiesAPI.letter(cid); w.document.open(); w.document.write(r.data); w.document.close(); }
  catch { w.close(); toast.error('Could not open the letter'); }
};
const openBankPdf = async (id) => {
  try { const r = await opportunitiesAPI.bankPdf(id); window.open(URL.createObjectURL(r.data), '_blank'); }
  catch { toast.error('Could not open the PDF'); }
};
const STATUS_LABEL = { committed: 'Committed', received: 'Deposit received', cancelled: 'Cancelled' };

/* Detail page: sits under the term card. */
export function FundItPanel({ o }) {
  const [open, setOpen] = useState(false);
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState({ amount: '', pledge_id: '', note: '', accept: false });
  const [busy, setBusy] = useState(false);
  const min = Number(o.minimum_investment_amount || 0);

  const loadMine = () => opportunitiesAPI.myCommitments(o.slug).then(r => setMine(r.data || [])).catch(() => {});
  useEffect(() => { if (o.fund_it) loadMine(); }, [o.slug, o.fund_it]); // eslint-disable-line react-hooks/exhaustive-deps
  // Tier cards on the detail page open this dialog preset (legacy «Select Contribution» → Continue)
  useEffect(() => {
    const onOpen = (e) => {
      const { pledge_id = '', amount = '' } = e.detail || {};
      setForm(f => ({ ...f, pledge_id, amount: amount ? String(amount) : f.amount }));
      setOpen(true);
    };
    window.addEventListener('fundit:open', onOpen);
    return () => window.removeEventListener('fundit:open', onOpen);
  }, []);

  if (!o.fund_it) return null;
  const pledges = [...(o.benefits || []), ...(o.backers || []), ...(o.services || [])].filter(p => p.title);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await opportunitiesAPI.commit(o.slug, { ...form, amount: Number(form.amount) });
      toast.success('Commitment sent. Check your email for the letter and bank details.');
      setOpen(false);
      setForm({ amount: '', pledge_id: '', note: '', accept: false });
      loadMine();
    } catch (ex) { toast.error(ex.response?.data?.detail || 'Could not register the commitment'); }
    finally { setBusy(false); }
  };
  const pickPledge = (id) => {
    const p = pledges.find(x => x.id === id);
    setForm(f => ({ ...f, pledge_id: id, amount: p?.price && !f.amount ? String(p.price) : f.amount }));
  };

  return (
    <div className="fundit rounded-[var(--op-radius)] border p-6 mt-4 bg-white" style={{ borderColor: 'rgba(20,26,58,.12)' }} data-testid="fund-it-panel">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[11px] font-semibold uppercase" style={{ letterSpacing: '.08em', color: 'rgba(20,26,58,.55)' }}>Committed so far</span>
        <span className="text-sm" style={{ color: 'rgba(20,26,58,.6)' }}>{o.backers_count} backer{o.backers_count === 1 ? '' : 's'}</span>
      </div>
      <div className="font-bold tabular-nums" style={{ fontFamily: 'var(--op-head)', fontSize: '1.8rem', color: 'var(--color-heading, #1a2332)' }}>{money(o.committed_total)}</div>
      {o.total_amount ? (
        <div className="h-1.5 rounded-full mt-2 mb-4" style={{ background: 'rgba(20,26,58,.08)' }}>
          <div className="h-full rounded-full" style={{ width: `${Math.min(100, (o.committed_total / o.total_amount) * 100)}%`, background: 'var(--color-accent, #ff9c1b)' }} />
        </div>
      ) : <div className="mb-4" />}
      {o.funding_open ? (
        <button type="button" onClick={() => setOpen(true)} className="w-full h-12 rounded-[var(--op-radius)] font-bold text-[15px] transition-transform active:scale-[.98]"
          style={{ background: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-ink, #16171a)' }} data-testid="fund-it-btn">Fund it</button>
      ) : <p className="text-sm" style={{ color: 'rgba(20,26,58,.6)' }}>Funding is closed.</p>}
      <p className="text-[12.5px] mt-3 leading-relaxed" style={{ color: 'rgba(20,26,58,.55)' }}>
        You commit an amount and receive a commitment letter with the bank details by email. The deposit is made by bank transfer.
      </p>
      {mine.length > 0 && (
        <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(20,26,58,.1)' }}>
          <span className="text-[11px] font-semibold uppercase" style={{ letterSpacing: '.08em', color: 'rgba(20,26,58,.55)' }}>Your commitments</span>
          {mine.map(c => (
            <div key={c.id} className="flex items-center justify-between gap-3 py-2 text-sm">
              <span><b className="tabular-nums">{money(c.amount)}</b> <span style={{ color: 'rgba(20,26,58,.55)' }}>· {STATUS_LABEL[c.status]} · ref {c.reference}</span></span>
              <button type="button" onClick={() => openLetter(c.id)} className="inline-flex items-center gap-1 font-semibold" style={{ color: 'var(--color-heading, #1a2332)' }}>Letter<ExternalLink className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Fund {o.name}</DialogTitle>
            <DialogDescription>We will email you a commitment letter with the bank details for the deposit.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid gap-4">
            {pledges.length > 0 && (
              <label className="grid gap-1.5 text-sm font-medium">Benefit or pledge (optional)
                <select className="h-11 px-3 border rounded-md" value={form.pledge_id} onChange={e => pickPledge(e.target.value)}>
                  <option value="">None</option>
                  {pledges.map(p => <option key={p.id} value={p.id}>{p.title}{p.price ? ` · ${money(p.price)}` : ''}</option>)}
                </select>
              </label>
            )}
            <label className="grid gap-1.5 text-sm font-medium">Amount (USD)
              <input type="number" required min={min || 1} step="any" className="h-11 px-3 border rounded-md tabular-nums" value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder={min ? `Minimum ${money(min)}` : ''} />
              {min > 0 && <span className="text-xs font-normal" style={{ color: 'rgba(20,26,58,.55)' }}>Minimum investment {money(min)}</span>}
            </label>
            <label className="grid gap-1.5 text-sm font-medium">Note to the founder (optional)
              <textarea rows={3} maxLength={1000} className="px-3 py-2 border rounded-md" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
            </label>
            <label className="flex gap-2.5 items-start text-sm">
              <input type="checkbox" className="mt-1" required checked={form.accept} onChange={e => setForm(f => ({ ...f, accept: e.target.checked }))} />
              <span>I commit to invest this amount and will make the deposit by bank transfer using the reference in my letter.</span>
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="h-11 px-4 text-sm">Cancel</button>
              <button type="submit" disabled={busy} className="h-11 px-5 rounded-md font-semibold text-sm inline-flex items-center gap-2"
                style={{ background: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-ink, #16171a)' }}>
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}Send my commitment
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* Editor: owner uploads the bank PDF, adds letter instructions and manages commitments. */
export function FundItEditor({ id, form, set, isNew }) {
  const [uploading, setUploading] = useState(false);
  const [data, setData] = useState(null);
  const inv = form.investment || {};
  const load = () => opportunitiesAPI.commitments(id).then(r => setData(r.data)).catch(() => setData({ rows: [], totals: {} }));
  useEffect(() => { if (!isNew) load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isNew) return <p className="text-sm" style={{ color: 'rgba(20,26,58,.55)' }}>Create the draft first, then upload the bank details.</p>;
  const upload = async (file) => {
    setUploading(true);
    try {
      const r = await opportunitiesAPI.upload(id, 'bank', file);
      set({ investment: { ...inv, bank_pdf: 'set', bank_pdf_name: r.data.bank_pdf_name, bank_pdf_at: r.data.bank_pdf_at } });
      toast.success('Bank details uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };
  const setStatus = async (c, status) => {
    try { await opportunitiesAPI.setCommitmentStatus(c.id, status); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };
  const t = data?.totals || {};
  return (
    <div className="grid gap-6" data-testid="fund-it-editor">
      <div className="grid gap-2">
        <span className="text-[12.5px] font-medium" style={{ color: 'var(--color-heading, #1a2332)' }}>Bank details (PDF)</span>
        <div className="flex items-center gap-3 flex-wrap">
          {inv.bank_pdf ? (
            <span className="inline-flex items-center gap-2 text-sm"><FileText className="w-4 h-4" />{inv.bank_pdf_name || 'bank-details.pdf'}</span>
          ) : <span className="text-sm" style={{ color: 'rgba(20,26,58,.55)' }}>Not uploaded. Fund it stays hidden until you add it.</span>}
          <label className="inline-flex items-center gap-2 h-10 px-4 border rounded-md text-sm font-semibold cursor-pointer">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{inv.bank_pdf ? 'Replace' : 'Upload PDF'}
            <input type="file" accept="application/pdf" hidden onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
          </label>
          {inv.bank_pdf && <button type="button" onClick={() => openBankPdf(id)} className="inline-flex items-center gap-1 text-sm font-semibold"><Download className="w-4 h-4" />View</button>}
        </div>
        <span className="text-xs" style={{ color: 'rgba(20,26,58,.55)' }}>Private: it is only sent attached to each investor's commitment letter.</span>
      </div>
      <div className="grid gap-2">
        <span className="text-[12.5px] font-medium" style={{ color: 'var(--color-heading, #1a2332)' }}>Instructions printed in the letter (optional)</span>
        <RichTextEditor value={inv.instructions || ''} onChange={v => set({ investment: { ...inv, instructions: v } })}
          placeholder="E.g. deadline for the transfer, who to contact, documents to send…" />
      </div>
      <div className="grid gap-3">
        <div className="flex flex-wrap gap-6 text-sm">
          <span>Committed <b className="tabular-nums">{money(t.committed)}</b></span>
          <span>Received <b className="tabular-nums">{money(t.received)}</b></span>
          <span>Backers <b className="tabular-nums">{t.count || 0}</b></span>
        </div>
        <div className="overflow-x-auto border rounded-md">
          <table className="w-full text-sm">
            <thead><tr className="text-left" style={{ color: 'rgba(20,26,58,.55)' }}>
              <th className="p-3 font-medium">Investor</th><th className="p-3 font-medium">Amount</th><th className="p-3 font-medium">Pledge</th>
              <th className="p-3 font-medium">Reference</th><th className="p-3 font-medium">Date</th><th className="p-3 font-medium">Status</th><th className="p-3" />
            </tr></thead>
            <tbody>
              {(data?.rows || []).map(c => (
                <tr key={c.id} className="border-t">
                  <td className="p-3">{c.investor_name}<div className="text-xs" style={{ color: 'rgba(20,26,58,.55)' }}>{c.investor_email}</div></td>
                  <td className="p-3 tabular-nums font-semibold">{money(c.amount)}</td>
                  <td className="p-3">{c.pledge_title || '—'}</td>
                  <td className="p-3 font-mono text-xs">{c.reference}</td>
                  <td className="p-3 whitespace-nowrap">{new Date(c.created_at).toLocaleDateString()}</td>
                  <td className="p-3">
                    <select className="h-9 px-2 border rounded-md" value={c.status} onChange={e => setStatus(c, e.target.value)}>
                      {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </td>
                  <td className="p-3"><button type="button" onClick={() => openLetter(c.id)} title="Open letter" aria-label="Open letter"><ExternalLink className="w-4 h-4" /></button></td>
                </tr>
              ))}
              {data && !data.rows.length && <tr><td colSpan={7} className="p-6 text-center" style={{ color: 'rgba(20,26,58,.55)' }}>No commitments yet.</td></tr>}
              {!data && <tr><td colSpan={7} className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>}
            </tbody>
          </table>
        </div>
        {(data?.rows || []).some(c => c.status === 'received') && (
          <p className="text-xs inline-flex items-center gap-1.5" style={{ color: 'rgba(20,26,58,.55)' }}><CheckCircle2 className="w-3.5 h-3.5" />Received deposits count toward the totals above.</p>
        )}
      </div>
    </div>
  );
}

export default FundItPanel;
